import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid2,
  Typography,
} from '@mui/material';
import {
  DefenseEventTypes,
  IDefenseEventGetByIdParams,
  IDefenseEventProps,
  IDefenseEventSimpleProps,
} from 'services/Defense/Events/types';
import DefenseEventDetailsModal from 'pages/DefensePages/Events/_components/DefenseEventDetailsModal';
import { useDefenseEventByIdAsync } from 'services/Defense/Events/hooks/useMutations';
import CarrouselImagesComponent from 'components/CarrouselImagesComponent/CarrouselImagesComponent';
import { useGetImagesAsync } from 'services/ImageService/hooks/useMutation';
import { convertEventType } from 'converters/Defense/ConverterDefense';
import { t } from 'i18next';

interface CardLastEventProps {
  events: IDefenseEventSimpleProps[];
  widthProps: string;
  iconProp?: React.ReactElement;
}

interface IEventDetailsList {
  label: string;
  value: any;
}

const CardLastEventDefense = ({
  events,
  widthProps,
  iconProp,
}: CardLastEventProps) => {
  const [eventDetails, setEventDetails] = React.useState<any[]>();
  const [isVisibleDetailsModal, setIsVisibleDetailsModal] =
    useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<IDefenseEventProps>();
  const { mutateAsync: mutateDefenseEventByIdAsync } =
    useDefenseEventByIdAsync();
  const [urls, setUrls] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const { mutateAsync: mutateGetImages } = useGetImagesAsync();

  useEffect(() => {
    if (events && events.length > 0) {
      const eventImages = events.flatMap((event) => event.images || []);
      setImages(eventImages);
    }
  }, [events]);

  const requestAll = async () => {
    await mutateGetImages(images).then(setImages);
  };

  useEffect(() => {
    if (images && images.length > 0) {
      const imageUrls = images.map((i) => `data:image/jpg;base64, ${i}`);
      setUrls(imageUrls);
    }
  }, [images]);

  useEffect(() => {
    if (images && images.length > 0) {
      requestAll();
    }
  }, [images]);

  const handleOpenModal = async (event: IDefenseEventSimpleProps) => {
    if (event?.id && event?.type) {
      if (event.type === 'ScreenAllEvents') {
        return;
      } else {
        await handleDetails(event.id, event.type);
        setIsModalOpen(true);
      }
    }
  };
  const handleDetails = async (id: number, type: string) => {
    await mutateDefenseEventByIdAsync({
      id: id,
      type: type,
    } as IDefenseEventGetByIdParams).then(setSelectedEvent);
    setIsVisibleDetailsModal(true);
  };

  const getValuesToRenderByType = (
    type: string,
    event: IDefenseEventSimpleProps,
  ): any => {
    if (!type) return [];
    const details: IEventDetailsList[] = [];
    if (event?.id && eventDetails) {
      for (const prop in eventDetails) {
        if (eventDetails[prop]) {
          if (prop === 'description') {
            details.push({
              label: prop,
              value: eventDetails[prop],
            });
          }
        }
      }
    }
    switch (type) {
      case 'DEFENSE_NOTIFY_DEVICE_STATUS':
      case 'DEFENSE_NOTIFY_DEVICE_CHANNEL_STATUS':
      case 'DEFENSE_HANDLE_ALARM':
      case 'DEFENSE_NOTIFY_ALARM_PICTURE':
      case 'DEFENSE_DOOR_ACCESS_STATUS':
      case 'DEFENSE_DOOR_GLOBAL_CONTROL_STATUS':
      case 'DEFENSE_DOOR_ALWAYS_OPEN_CLOSE_STATUS':
      case 'DEFENSE_ADD_PERSON':
      case 'DEFENSE_UPDATE_PERSON':
      case 'DEFENSE_DELETE_PERSON':
      case 'DEFENSE_DOOR_ACCESS_CAPTURE_FINGER_PRINT':
      case 'DEFENSE_NOTIFY_ACS_EVENT':
      case 'DEFENSE_OPEN_SLUICE':
      case 'DEFENSE_NOTIFY_ALARMS':
      case 'DEFENSE_NOTIFY_ADD_VISITOR':
      case 'DEFENSE_NOTIFY_UPDATE_VISITOR':
      case 'DEFENSE_NOTIFY_DELETE_VISITORS':
      case 'DEFENSE_NOTIFY_VISITOR_COUNT_REPORT':
      case 'DEFENSE_NOTIFY_VEHICLE_CAPTURE_INFO':
      case 'DEFENSE_NOTIFY_FACE_INFOS':
      case 'DEFENSE_NOTIFY_HUMAN_INFOS':
      case 'DEFENSE_NOTIFY_MOTOR_VEHICLE_INFOS':
      case 'DEFENSE_NOTIFY_NO_MOTOR_VEHICLE_INFOS':
      case 'DEFENSE_NOTIFY_AREA_ARM_STATUS':
      case 'DEFENSE_NOTIFY_ZONE_BY_PASS_STATUS':
      case 'DEFENSE_NOTIFY_ZONE_FAULT_STATUS':
      case 'DEFENSE_NOTIFY_SYNC_DEVICE_INFO':
        return [
          { label: t('common.date'), value: event.createdAt },
          { label: t('common.description'), value: event.description },
        ];
      default:
        return [];
    }
  };

  const renderEventDetails = (event: IDefenseEventSimpleProps) => {
    if (!event || !event.type) {
      return <Typography>{t('dontHaveEvents')}</Typography>;
    }

    const valuesToRender: IEventDetailsList[] =
      getValuesToRenderByType(event.type, event) || [];

    return valuesToRender.map((item, index) => (
      <Box key={index}>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'flex-start'}
          justifyContent={'start'}
        >
          <Box>
            <Typography sx={{ fontWeight: 'bold', color: '#4967a6' }}>
              {item.label}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={'13px'}>{item.value}</Typography>
          </Box>
        </Box>
      </Box>
    ));
  };

  return (
    <Box>
      {events.map((event, index) => (
        <CardActionArea key={index}>
          <>
            <Card
              onClick={() => handleOpenModal(event)}
              sx={{
                width: '100%',
                Height: '52vh',
                p: 2,
                backgroundColor: '#202940',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Grid2
                width={'90%'}
                position={'absolute'}
                display={'flex'}
                justifyContent={'end'}
                top={10}
                right={10}
              >
                <Box>
                  <Chip
                    icon={iconProp}
                    color="primary"
                    label={convertEventType(event.type as DefenseEventTypes)}
                    sx={{
                      fontSize: window.innerWidth < 900 ? '10px' : 'inherit',
                      height: window.innerWidth < 900 ? '24px' : 'inherit',
                    }}
                  />
                </Box>
              </Grid2>
              <Grid2
                sx={{
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  justifyContent: 'start',
                  alignItems: 'center',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <CarrouselImagesComponent height="20vh" items={urls} />
              </Grid2>
              <Grid2
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  margin: 1,
                  padding: 1,
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  alignItems={'left'}
                  textAlign={'left'}
                  width={'100%'}
                >
                  {renderEventDetails(event)}
                </Box>
              </Grid2>
            </Card>
            <DefenseEventDetailsModal
              isOpen={isModalOpen}
              setIsOpen={setIsModalOpen}
              selectedEvent={selectedEvent}
              setSelectedEvent={setSelectedEvent}
            />
          </>
        </CardActionArea>
      ))}
    </Box>
  );
};

export default CardLastEventDefense;
